import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Button,
  Alert,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Message as MessageIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';

const MessageDetailsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username } = location.state || {};
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [smsText, setSmsText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!username) {
      navigate('/messages');
      return;
    }
    loadData();
  }, [username]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [items]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/message/get-transacted-messages?username=${username}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setItems(response.data || []);
    } catch (error) {
      console.error('MessageDetailsScreen loadData error:', error);
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    await loadData();
  };

  const confirmDelete = (id) => {
    setDeleteItemId(id);
    setDeleteDialogOpen(true);
  };

  const deleteItem = async () => {
    if (!deleteItemId) return;

    try {
      setDeletingId(deleteItemId);
      setDeleteDialogOpen(false);
      const token = localStorage.getItem('userToken');

      await api.delete(`api/school-common/message/delete-message?id=${deleteItemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Remove item from local state
      setItems((prevItems) => prevItems.filter((item) => item.id !== deleteItemId));
      setNotification({ open: true, message: 'Message deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('MessageDetailsScreen deleteItem error:', error);
      handleCatch(error);
    } finally {
      setDeletingId(null);
      setDeleteItemId(null);
    }
  };

  const sendMessage = async () => {
    if (!username) {
      setNotification({ open: true, message: 'Username is required to send message!', severity: 'error' });
      return;
    }
    if (!smsText?.trim()) {
      setNotification({ open: true, message: 'Message required!', severity: 'error' });
      return;
    }

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('userToken');
      const formData = new FormData();
      formData.append('ToUserName', username);
      formData.append('Description', smsText.trim());

      await api.post('api/school-common/message/send-message-to-anyone', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setNotification({ open: true, message: `Message sent to ${username}`, severity: 'success' });
      setSmsText('');
      await loadData();
    } catch (error) {
      console.error('MessageDetailsScreen sendMessage error:', error);
      handleCatch(error);
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1)
      return (
        'Yesterday ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    if (diffDays < 7)
      return (
        date.toLocaleDateString() +
        ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    return (
      date.toLocaleDateString() +
      ' ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title={username || 'Messages'}
        onBack={() => navigate('/messages')}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#EBEBEB',
          px: 1.5,
          py: 1.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 6 }}>
            <MessageIcon sx={{ fontSize: 56, color: '#ccc', mb: 2 }} />
            <Typography sx={{ fontWeight: 600, color: '#888', fontSize: 15 }}>No Messages Yet</Typography>
            <Typography sx={{ fontSize: 12, color: '#bbb', mt: 0.5 }}>Start a conversation with {username}.</Typography>
          </Box>
        ) : (
          <>
            {items.map((item) => {
              const isReceived = item.type === 'R';
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isReceived ? 'flex-start' : 'flex-end',
                    mb: 1,
                  }}
                >
                  {isReceived && (
                    <Avatar sx={{ width: 28, height: 28, bgcolor: '#ccc', mr: 0.75, mt: 0.5, flexShrink: 0 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: '75%',
                      bgcolor: isReceived ? '#fff' : colors.BLUE,
                      color: isReceived ? '#222' : '#fff',
                      borderRadius: isReceived ? '2px 14px 14px 14px' : '14px 2px 14px 14px',
                      px: 1.5,
                      pt: 1,
                      pb: 0.75,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>{item.description}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                      <Typography sx={{ fontSize: 10, color: isReceived ? '#aaa' : 'rgba(255,255,255,0.6)' }}>
                        {formatDate(item.transactDate)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => confirmDelete(item.id)}
                        disabled={deletingId === item.id}
                        sx={{ p: 0, color: isReceived ? '#ccc' : 'rgba(255,255,255,0.5)' }}
                      >
                        {deletingId === item.id
                          ? <CircularProgress size={10} sx={{ color: 'inherit' }} />
                          : <DeleteIcon sx={{ fontSize: 12 }} />}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input Bar */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: '#fff',
          borderTop: '1px solid #eee',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type a message..."
          value={smsText}
          onChange={(e) => setSmsText(e.target.value)}
          disabled={sendingMessage}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: '#f8f8f8',
              fontSize: 14,
            },
          }}
        />
        <Box
          component="button"
          onClick={sendMessage}
          disabled={!smsText?.trim() || sendingMessage}
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: smsText?.trim() && !sendingMessage ? colors.BLUE : '#ccc',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: smsText?.trim() && !sendingMessage ? 'pointer' : 'default',
            flexShrink: 0,
            mb: 0.25,
          }}
        >
          {sendingMessage
            ? <CircularProgress size={20} sx={{ color: '#fff' }} />
            : <SendIcon sx={{ color: '#fff', fontSize: 20 }} />}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this message?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteItem} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageDetailsScreen;
      {/* Header */}
